'use client';

import { useAtomValue, useSetAtom } from 'jotai';
import Image from 'next/image';
import type { ReactNode } from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { aiChatOpenAtom, centerToastModalAtom, zzimcarToastAtom } from '@/atom/modal';
import { globalTripAtom, globalTripDestinationsAtom } from '@/atom/trip';
import type { components } from '@/types/ai-api';
import type { SearchResultCGType } from '@/types/graphql/root';
import { filterNullUndefined, getSortedDestinationList } from '@/utils';
import { sanitizeAssistantContent } from '@/utils/ai-content';

type ChatMessageDto = components['schemas']['ChatMessageDto'];
type ChatSessionWithMessagesDto = components['schemas']['ChatSessionWithMessagesDto'];

import { useAiContext } from '@/app/hooks/useAiContext';
import { addDestinations, getPoiDetailByPlaceId, removeSlot, reorderAllDestinations } from '@/utils/actions';
import { createAiApi, getUserIdForAi } from '@/utils/ai-client';
import SparkleIcon from './sparkle-icon';

const SEMANTIC_SUPPORTED_ZONE_IDS = new Set<string>([
  'w1laG8bqX6',
  '9gl7PDb2DP',
  'pVlpx9GlZP',
  'XPN6P9620J',
  'kVldRzBqbe',
  'K1lR9pjNmn',
  'K1lR3rj2mn',
]);

type DestinationCandidate = {
  id: string;
  day: number;
  name: string;
};

type PoiDetailsCandidate = {
  placeId: string;
  name: string;
  day?: number;
};

type AskWhichCandidate = DestinationCandidate | PoiDetailsCandidate;

type UiActionType = 'DELETE' | 'SWAP' | 'REORDER' | 'GET_POI_DETAILS';

interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  isTyping?: boolean;
  displayedContent?: string;
  metadata?: {
    searchResults?: SearchResultCGType[];
    actions?: ChatServerAction[];
    day?: number;
    ui?: {
      kind: string;
      totalDays?: number;
      actionType?: UiActionType;
      candidates?: AskWhichCandidate[];
    };
    pendingAction?: ChatServerAction;
    previewImageUrl?: string;
    previewImageSourceUrl?: string;
  };
}

type ChatServerActionType =
  | 'REORDER'
  | 'SEMANTIC_SEARCH_POI'
  | 'SEARCH_POI'
  | 'DELETE'
  | 'SWAP'
  | 'UPDATE_TIME'
  | 'PLAN_FULL_DAY';

interface ChatServerAction {
  type: ChatServerActionType;
  payload: unknown;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function extractActionsFromMetadata(metadata: unknown): ChatServerAction[] {
  if (!isRecord(metadata)) return [];
  const actions = metadata.actions;
  if (!Array.isArray(actions)) return [];

  const parsed: ChatServerAction[] = [];
  for (const item of actions) {
    if (!isRecord(item)) continue;
    const type = item.type;
    const payload = item.payload;
    if (typeof type !== 'string') continue;
    parsed.push({ type: type as ChatServerActionType, payload });
  }
  return parsed;
}

function coerceNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const n = Number(value);
    return Number.isFinite(n) ? n : null;
  }
  return null;
}

function renderMessageContent(text: string, role: 'user' | 'assistant'): ReactNode {
  const linkClassName =
    role === 'user'
      ? 'underline underline-offset-2 text-white'
      : 'underline underline-offset-2 text-blue-700 hover:text-blue-800';

  const renderInlineTokens = (segment: string, lineIndex: number, segmentOffset: number): ReactNode[] => {
    const nodes: ReactNode[] = [];
    const tokenRegex = /\[([^\]]+)\]\s*\(\s*(https?:\/\/[^\s)]+)\s*\)|(https?:\/\/[^\s)]+)/g;
    let lastIndex = 0;

    for (let match = tokenRegex.exec(segment); match; match = tokenRegex.exec(segment)) {
      const start = match.index;
      if (start > lastIndex) {
        nodes.push(segment.slice(lastIndex, start));
      }

      const mdLabel = match[1];
      const mdUrl = match[2];
      const rawUrl = match[3];
      const url = mdUrl || rawUrl;
      const label = mdLabel || rawUrl;

      if (url) {
        nodes.push(
          <a
            key={`msg-link-${lineIndex}-${segmentOffset + start}`}
            href={url}
            target='_blank'
            rel='noopener noreferrer'
            className={linkClassName}
          >
            {label}
          </a>,
        );
      }

      lastIndex = start + match[0].length;
    }

    if (lastIndex < segment.length) {
      nodes.push(segment.slice(lastIndex));
    }

    return nodes;
  };

  const renderLine = (line: string, lineIndex: number): ReactNode[] => {
    const nodes: ReactNode[] = [];
    const boldRegex = /\*\*(.+?)\*\*/g;
    let lastIndex = 0;

    for (let match = boldRegex.exec(line); match; match = boldRegex.exec(line)) {
      const start = match.index;
      if (start > lastIndex) {
        nodes.push(...renderInlineTokens(line.slice(lastIndex, start), lineIndex, lastIndex));
      }

      nodes.push(
        <strong key={`msg-bold-${lineIndex}-${start}`} className='font-semibold text-gray-800'>
          {match[1]}
        </strong>,
      );

      lastIndex = start + match[0].length;
    }

    if (lastIndex < line.length) {
      nodes.push(...renderInlineTokens(line.slice(lastIndex), lineIndex, lastIndex));
    }

    return nodes;
  };

  const lines = text.split('\n');
  const out: ReactNode[] = [];
  for (let i = 0; i < lines.length; i += 1) {
    out.push(...renderLine(lines[i], i));
    if (i < lines.length - 1) out.push(<br key={`msg-br-${i}`} />);
  }
  return out;
}

function toSearchResults(value: unknown): SearchResultCGType[] {
  if (!Array.isArray(value)) return [];
  return value
    .map((item) => {
      if (!isRecord(item)) return null;
      const rawMainImageUrl = typeof item.mainImageUrl === 'string' ? item.mainImageUrl : undefined;
      const shouldIgnoreGenericIcon =
        typeof item.poi !== 'object' || item.poi === null
          ? Boolean(
              rawMainImageUrl &&
                /^(?:https?:\/\/)?maps\.gstatic\.com\/mapfiles\/place_api\/icons\//i.test(rawMainImageUrl),
            )
          : false;
      const mainImageUrl = shouldIgnoreGenericIcon ? undefined : rawMainImageUrl;
      const placeId =
        typeof item.placeId === 'string' ? item.placeId : typeof item.poiId === 'string' ? item.poiId : undefined;
      const result: SearchResultCGType = {
        placeId,
        name: typeof item.name === 'string' ? item.name : undefined,
        address: typeof item.address === 'string' ? item.address : undefined,
        poiType: typeof item.poiType === 'string' ? item.poiType : undefined,
        latitude: typeof item.latitude === 'number' ? item.latitude : undefined,
        longitude: typeof item.longitude === 'number' ? item.longitude : undefined,
        mainImageUrl,
      };
      return result;
    })
    .filter((result): result is SearchResultCGType => Boolean(result));
}

function toConversationMessage(msg: ChatMessageDto): ConversationMessage {
  const actions = msg.role === 'assistant' ? extractActionsFromMetadata(msg.metadata) : [];
  let searchResults: SearchResultCGType[] | undefined;
  let day: number | undefined;
  let ui:
    | {
        kind: string;
        totalDays?: number;
        actionType?: UiActionType;
        candidates?: AskWhichCandidate[];
      }
    | undefined;

  if (actions.length > 0) {
    const recAction = actions.find((a) => a.type === 'SEMANTIC_SEARCH_POI' || a.type === 'SEARCH_POI');
    if (recAction && isRecord(recAction.payload)) {
      const sr = toSearchResults(recAction.payload.search_results).slice(0, 5);
      if (sr.length > 0) searchResults = sr;

      const d = coerceNumber(recAction.payload.day);
      if (d) day = d;
    }
  }

  if (msg.role === 'assistant' && isRecord(msg.metadata)) {
    const metaUi = msg.metadata.ui;
    if (isRecord(metaUi) && typeof metaUi.kind === 'string') {
      const candidates = Array.isArray(metaUi.candidates)
        ? metaUi.candidates
            .map((c): AskWhichCandidate | null => {
              if (!isRecord(c)) return null;
              if (typeof c.id === 'string' && typeof c.day === 'number' && typeof c.name === 'string') {
                return { id: c.id, day: c.day, name: c.name };
              }
              if (typeof c.placeId === 'string' && typeof c.name === 'string') {
                return {
                  placeId: c.placeId,
                  name: c.name,
                  day: typeof c.day === 'number' ? c.day : undefined,
                };
              }
              return null;
            })
            .filter((c): c is AskWhichCandidate => c !== null)
        : undefined;

      ui = {
        kind: metaUi.kind,
        totalDays: typeof metaUi.totalDays === 'number' ? metaUi.totalDays : undefined,
        actionType:
          typeof metaUi.actionType === 'string' &&
          ['DELETE', 'SWAP', 'REORDER', 'GET_POI_DETAILS'].includes(metaUi.actionType)
            ? (metaUi.actionType as UiActionType)
            : undefined,
        candidates,
      };
    }
  }

  let pendingAction: ChatServerAction | undefined;
  if (msg.role === 'assistant' && isRecord(msg.metadata) && isRecord(msg.metadata.pendingAction)) {
    const pa = msg.metadata.pendingAction;
    if (typeof pa.type === 'string' && ['REORDER', 'DELETE', 'SWAP'].includes(pa.type) && isRecord(pa.payload)) {
      pendingAction = pa as unknown as ChatServerAction;
    }
  }

  const previewImageUrl =
    msg.role === 'assistant' && isRecord(msg.metadata) && typeof msg.metadata.previewImageUrl === 'string'
      ? msg.metadata.previewImageUrl
      : undefined;
  const previewImageSourceUrl =
    msg.role === 'assistant' && isRecord(msg.metadata) && typeof msg.metadata.previewImageSourceUrl === 'string'
      ? msg.metadata.previewImageSourceUrl
      : undefined;

  const content = msg.role === 'assistant' ? sanitizeAssistantContent(msg.content) : msg.content;

  return {
    id: msg.id,
    role: msg.role === 'user' ? 'user' : 'assistant',
    content,
    metadata:
      msg.role === 'assistant'
        ? {
            actions,
            searchResults,
            day,
            ui,
            pendingAction,
            previewImageUrl,
            previewImageSourceUrl,
          }
        : undefined,
  };
}

const CHAT_SESSION_TTL_MS = 24 * 60 * 60 * 1000;

function getLastChatAt(session: ChatSessionWithMessagesDto): string | null {
  if (session.messages.length > 0) {
    return session.messages.reduce<string>(
      (latest, m) => (m.createdAt > latest ? m.createdAt : latest),
      session.messages[0].createdAt,
    );
  }
  return session.updatedAt;
}

export function AiAssistantButton() {
  const setIsChatOpen = useSetAtom(aiChatOpenAtom);

  return (
    <button
      onClick={() => setIsChatOpen(true)}
      className='group hidden md:flex absolute bottom-20 right-4 z-50 h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-sky-300 via-sky-400 to-blue-400 shadow-lg shadow-sky-400/40 hover:shadow-xl hover:shadow-sky-400/60 active:scale-95 hover:scale-105 transition-all duration-200'
      style={{
        boxShadow:
          '0 4px 14px 0 rgba(125, 211, 252, 0.4), inset 0 -2px 6px 0 rgba(14, 165, 233, 0.3), inset 0 2px 6px 0 rgba(255, 255, 255, 0.5)',
      }}
      aria-label='AI 어시스턴트 열기'
    >
      <SparkleIcon size={24} />
    </button>
  );
}

export function AiAssistantPanel() {
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [typingMessageId, setTypingMessageId] = useState<string | null>(null);
  const [expandedDayPickerId, setExpandedDayPickerId] = useState<string | null>(null);
  const [resultPageByMessageId, setResultPageByMessageId] = useState<Record<string, number>>({});
  const [swapSelectedIndices, setSwapSelectedIndices] = useState<number[]>([]);
  const [pendingAddPoi, setPendingAddPoi] = useState<{
    messageId: string;
    poi: SearchResultCGType;
    suggestedDay?: number;
  } | null>(null);

  const messagesEndRef = useRef<HTMLDivElement>(null);
  const typingTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const isSendingRef = useRef(false);
  const isPendingAddPlaceRef = useRef(false);

  const destinations = useAtomValue(globalTripDestinationsAtom);
  const currentSurvey = useAtomValue(globalTripAtom);
  const setDestinations = useSetAtom(globalTripDestinationsAtom);
  const setIsChatOpen = useSetAtom(aiChatOpenAtom);
  const setCenterToastModal = useSetAtom(centerToastModalAtom);
  const setZzimcarToast = useSetAtom(zzimcarToastAtom);
  const aiContext = useAiContext();

  const isSemanticSupportedZone = useMemo(() => {
    const cities = currentSurvey?.cities ?? [];
    return cities.some((city) => {
      const zoneId = city?.zone?.id;
      return typeof zoneId === 'string' && SEMANTIC_SUPPORTED_ZONE_IDS.has(zoneId);
    });
  }, [currentSurvey?.cities]);

  const chatApi = useMemo(() => createAiApi(), []);
  const isChatOpen = useAtomValue(aiChatOpenAtom);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [conversationHistory, setConversationHistory] = useState<ConversationMessage[]>([]);
  const [lastChatAt, setLastChatAt] = useState<string | null>(null);
  const [chatUserId, setChatUserId] = useState<string | null>(null);
  const initializedSurveyIdRef = useRef<string | null>(null);
  const isInitializingRef = useRef(false);
  const sessionVersionRef = useRef(0);

  const lastUserMessageIndex = useMemo(() => {
    for (let i = conversationHistory.length - 1; i >= 0; i -= 1) {
      if (conversationHistory[i].role === 'user') return i;
    }
    return -1;
  }, [conversationHistory]);

  const isPromptActive = (messageIndex: number) => messageIndex > lastUserMessageIndex;

  const totalDays = useMemo(() => {
    if (!currentSurvey?.fromDateTime || !currentSurvey?.toDateTime) return 1;

    const diff = new Date(currentSurvey.toDateTime).getTime() - new Date(currentSurvey.fromDateTime).getTime();
    return Math.max(1, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  }, [currentSurvey?.fromDateTime, currentSurvey?.toDateTime]);

  const isDaySelectionPrompt = (msg: ConversationMessage) => {
    if (msg.role !== 'assistant') return false;
    return msg.metadata?.ui?.kind === 'ASK_DAY';
  };

  const isWhichSelectionPrompt = (msg: ConversationMessage) => {
    if (msg.role !== 'assistant') return false;
    return msg.metadata?.ui?.kind === 'ASK_WHICH';
  };

  const isConfirmationPrompt = (msg: ConversationMessage) => {
    if (msg.role !== 'assistant') return false;
    return msg.metadata?.ui?.kind === 'ASK_CONFIRM';
  };

  const createNewSession = async (surveyId: string, userId: string) => {
    const { data: session, error } = await chatApi.POST('/api/chat/sessions', {
      body: {
        surveyId,
        tripName: currentSurvey?.customName ?? undefined,
        cities: (currentSurvey?.cities ?? []).map((c) => c?.name).filter((v): v is string => Boolean(v)),
        fromDate: currentSurvey?.fromDateTime ?? undefined,
        toDate: currentSurvey?.toDateTime ?? undefined,
      },
      headers: {
        'x-user-id': userId,
      },
    });

    if (error || !session) {
      throw new Error('Failed to create chat session');
    }

    setSessionId(session.id);
    return session.id;
  };

  const resetUiForNewSession = (_reason: 'ttl' | 'manual') => {
    sessionVersionRef.current += 1;
    isPendingAddPlaceRef.current = false;

    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }
    setTypingMessageId(null);
    setIsLoading(false);
    setSessionId(null);
    setConversationHistory([]);
    setPendingAddPoi(null);
    setLastChatAt(null);
    setUserInput('');
  };

  const startNewSession = async (reason: 'ttl' | 'manual') => {
    const surveyId = currentSurvey?.id;
    if (!surveyId) return null;

    resetUiForNewSession(reason);

    const userId = chatUserId ?? (await getUserIdForAi());
    setChatUserId(userId);

    try {
      return await createNewSession(surveyId, userId);
    } catch (error) {
      console.error('Failed to create new chat session:', error);
      return null;
    }
  };

  useEffect(() => {
    const surveyId = currentSurvey?.id;
    if (!surveyId) return;

    if (initializedSurveyIdRef.current && initializedSurveyIdRef.current !== surveyId) {
      initializedSurveyIdRef.current = null;
      resetUiForNewSession('manual');
      setExpandedDayPickerId(null);
      setSwapSelectedIndices([]);
      setIsChatOpen(false);
    }
  }, [currentSurvey?.id]);

  useEffect(() => {
    const surveyId = currentSurvey?.id;
    if (!isChatOpen || !surveyId) return;
    if (initializedSurveyIdRef.current === surveyId) return;
    if (isInitializingRef.current) return;

    let cancelled = false;
    isInitializingRef.current = true;

    const init = async () => {
      try {
        const userId = chatUserId ?? (await getUserIdForAi());
        if (cancelled) return;
        setChatUserId(userId);

        let sessionWithMessages: ChatSessionWithMessagesDto | null = null;
        try {
          const { data, error } = await chatApi.GET('/api/chat/sessions/{surveyId}', {
            params: {
              path: { surveyId },
            },
            headers: {
              'x-user-id': userId,
            },
          });
          sessionWithMessages = error || !data ? null : data;
        } catch {
          sessionWithMessages = null;
        }
        if (cancelled) return;

        const last = sessionWithMessages ? getLastChatAt(sessionWithMessages) : null;
        const expired = last ? Date.now() - new Date(last).getTime() > CHAT_SESSION_TTL_MS : false;

        if (!sessionWithMessages || expired) {
          await startNewSession(expired ? 'ttl' : 'manual');
          initializedSurveyIdRef.current = surveyId;
          return;
        }

        setSessionId(sessionWithMessages.id);
        setLastChatAt(last);
        setConversationHistory(sessionWithMessages.messages.slice(-50).map(toConversationMessage));
        initializedSurveyIdRef.current = surveyId;
      } catch (error) {
        console.error('Failed to init chat:', error);
        if (!cancelled) {
          await startNewSession('manual');
          initializedSurveyIdRef.current = surveyId;
        }
      } finally {
        isInitializingRef.current = false;
      }
    };

    void init();

    return () => {
      cancelled = true;
      isInitializingRef.current = false;
    };
  }, [isChatOpen, currentSurvey?.id]);

  const ensureActiveSessionForSend = async (): Promise<string | null> => {
    const surveyId = currentSurvey?.id;
    if (!surveyId) return null;

    const expired = lastChatAt ? Date.now() - new Date(lastChatAt).getTime() > CHAT_SESSION_TTL_MS : false;
    if (!sessionId || expired) {
      return await startNewSession(expired ? 'ttl' : 'manual');
    }
    return sessionId;
  };

  useEffect(() => {
    const t = setTimeout(() => {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }, 0);
    return () => clearTimeout(t);
  }, [conversationHistory, isLoading, pendingAddPoi]);

  useEffect(() => {
    return () => {
      if (typingTimerRef.current) {
        clearInterval(typingTimerRef.current);
        typingTimerRef.current = null;
      }
    };
  }, []);

  const handleReorder = async (day: number, newOrderIds: string[]) => {
    if (!currentSurvey?.id || !destinations) return;

    const result = await reorderAllDestinations(currentSurvey.id, [{ day, destinationIds: newOrderIds }]);

    if (result.data) {
      const reorderedDests = filterNullUndefined(result.data);
      setDestinations((prev) => {
        if (!prev) return reorderedDests;
        const otherDests = prev.filter((d) => !reorderedDests.find((fd) => fd.id === d.id));
        return getSortedDestinationList([...otherDests, ...reorderedDests]);
      });
    }
  };

  const handleSwap = async (destId1: string, destId2: string) => {
    if (!currentSurvey?.id || !destinations) return;

    const dest1 = destinations.find((d) => d.id === destId1);
    const dest2 = destinations.find((d) => d.id === destId2);
    if (!dest1?.id || !dest2?.id || dest1.day == null || dest2.day == null) return;

    const affectedDays = new Set([dest1.day, dest2.day]);
    const dayDestinations = Array.from(affectedDays).map((day) => {
      const sorted = destinations
        .filter((d): d is typeof d & { id: string } => d.day === day && typeof d.id === 'string')
        .sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0));

      const destinationIds = sorted.map((d) => {
        if (d.id === destId1) return destId2;
        if (d.id === destId2) return destId1;
        return d.id;
      });

      return { day, destinationIds };
    });

    const result = await reorderAllDestinations(currentSurvey.id, dayDestinations);

    if (result.data) {
      const reorderedDests = filterNullUndefined(result.data);
      setDestinations((prev) => {
        if (!prev) return reorderedDests;
        const otherDests = prev.filter((d) => !reorderedDests.find((fd) => fd.id === d.id));
        return getSortedDestinationList([...otherDests, ...reorderedDests]);
      });
    }
  };

  const handleDelete = async (destId: string) => {
    if (!currentSurvey?.id || !destinations) {
      console.error('handleDelete: Missing surveyId or destinations');
      return;
    }

    const destination = destinations.find((d) => d.id === destId);
    if (!destination) {
      console.error('handleDelete: Destination not found:', destId);
      return;
    }

    const removedDests = await removeSlot(currentSurvey.id, [
      {
        id: destId,
        day: destination.day,
        placeId: destination.poi?.placeId,
      },
    ]);

    if (removedDests) {
      setDestinations((prev) => {
        if (!prev) return [];
        const updated = prev.filter((d) => d.id !== destId);
        return getSortedDestinationList(updated);
      });
    } else {
      console.error('handleDelete: removeSlot returned null/undefined');
    }
  };

  const handleAddPoi = async (poi: SearchResultCGType, day: number) => {
    if (!currentSurvey?.id || !destinations) return;

    const dayDestinations = destinations
      .filter((d) => d.day === day && !d.accommType)
      .sort((a, b) => (a.pos ?? 0) - (b.pos ?? 0));
    const lastPos = dayDestinations.length > 0 ? (dayDestinations[dayDestinations.length - 1].pos ?? 0) : 0;
    const pos = lastPos + 1;

    const result = await addDestinations(
      currentSurvey.id,
      [
        {
          day: day,
          placeId: poi.placeId || poi.poi?.placeId,
          pos: pos,
          type: poi.poiType || poi.poi?.poiType,
        },
      ],
      pos,
      1,
    );

    if (result.data) {
      const newDests = getSortedDestinationList(filterNullUndefined(result.data));
      setDestinations((prev) => {
        if (!prev) return newDests;
        return getSortedDestinationList([...prev, ...newDests]);
      });

      const addedDest = newDests[0];
      if (addedDest?.id) {
        const addedName = poi.name || addedDest.poi?.name || '장소';
        setZzimcarToast({
          visible: true,
          text: `${addedName}을(를) ${day}일차에 추가했어요!`,
          renderingTime: 2000,
          type: 'info',
        });

        setTimeout(() => {
          const $innerItinerary = document.getElementById('planner-inner-itinerary');
          const $dayContainer = document.getElementById(`dayContainer_${day}`);
          if (!$innerItinerary || !$dayContainer) return;
          $innerItinerary.scrollTo({
            top: $dayContainer.offsetTop + $dayContainer.offsetHeight - $innerItinerary.clientHeight,
            behavior: 'smooth',
          });
        }, 100);
      }
    }
  };

  const extractDayOnlySelection = (text: string): number | null => {
    const trimmed = text.trim();
    if (!trimmed) return null;
    const match = trimmed.match(/^(\d+)\s*(?:일차)?$/);
    if (!match) return null;
    const day = parseInt(match[1], 10);
    return Number.isFinite(day) ? day : null;
  };

  const confirmAddPoiToDay = (poi: SearchResultCGType, day: number) => {
    setPendingAddPoi(null);
    const assistantMsgId = `${Date.now()}-add-poi-confirm`;
    const poiName = poi.name || '선택한 장소';
    setConversationHistory((prev) => [
      ...prev,
      {
        id: assistantMsgId,
        role: 'assistant',
        content: `좋아요. "${poiName}"을(를) ${day}일차에 추가할게요!`,
      },
    ]);
    void handleAddPoi(poi, day);
  };

  const startTypingAnimation = (messageId: string, fullText: string) => {
    if (typingTimerRef.current) {
      clearInterval(typingTimerRef.current);
      typingTimerRef.current = null;
    }

    setTypingMessageId(messageId);
    setConversationHistory((prev) =>
      prev.map((msg) => {
        if (typingMessageId && msg.id === typingMessageId) {
          return { ...msg, isTyping: false, displayedContent: undefined };
        }
        if (msg.id === messageId) {
          return { ...msg, isTyping: true, displayedContent: '' };
        }
        return msg;
      }),
    );

    let idx = 0;

    typingTimerRef.current = setInterval(() => {
      idx += 1;
      setConversationHistory((prev) =>
        prev.map((msg) => (msg.id === messageId ? { ...msg, displayedContent: fullText.slice(0, idx) } : msg)),
      );

      if (idx >= fullText.length) {
        if (typingTimerRef.current) {
          clearInterval(typingTimerRef.current);
          typingTimerRef.current = null;
        }
        setTypingMessageId(null);
        setConversationHistory((prev) =>
          prev.map((msg) => (msg.id === messageId ? { ...msg, isTyping: false, displayedContent: undefined } : msg)),
        );
      }
    }, 15);
  };

  const handleSendMessage = async (overrideMessage?: string) => {
    const rawUserMessage = (overrideMessage ?? userInput).trim();
    if (!rawUserMessage) return;
    if (isLoading || isSendingRef.current) return;

    const isPendingAdd = isPendingAddPlaceRef.current && overrideMessage === undefined;
    if (isPendingAddPlaceRef.current) isPendingAddPlaceRef.current = false;

    const currentUserMessage = isPendingAdd ? `${rawUserMessage} 추가해줘` : rawUserMessage;

    if (pendingAddPoi) {
      if (currentUserMessage === '취소') {
        setPendingAddPoi(null);
        const assistantMsgId = `${Date.now()}-add-poi-cancel`;
        setConversationHistory((prev) => [
          ...prev,
          { id: `${Date.now()}-user-cancel`, role: 'user', content: currentUserMessage },
          { id: assistantMsgId, role: 'assistant', content: '알겠어요. 추가를 취소했어요.' },
        ]);
        setUserInput('');
        return;
      }

      const dayOnly = extractDayOnlySelection(currentUserMessage);
      if (dayOnly !== null) {
        if (dayOnly < 1 || dayOnly > totalDays) {
          const errorMsgId = `${Date.now()}-add-poi-day-error`;
          setConversationHistory((prev) => [
            ...prev,
            { id: `${Date.now()}-user-day`, role: 'user', content: currentUserMessage },
            {
              id: errorMsgId,
              role: 'assistant',
              content: `이 여행은 ${totalDays}일 일정이에요. 1일차부터 ${totalDays}일차 중에서 선택해주세요!`,
            },
          ]);
          setUserInput('');
          return;
        }

        const userMsgId = `${Date.now()}-user-day`;
        setConversationHistory((prev) => [...prev, { id: userMsgId, role: 'user', content: currentUserMessage }]);
        setUserInput('');
        confirmAddPoiToDay(pendingAddPoi.poi, dayOnly);
        return;
      }
    }

    isSendingRef.current = true;

    try {
      if (!currentSurvey?.id) {
        const errorMsgId = Date.now().toString();
        setConversationHistory((prev) => {
          const message: ConversationMessage = {
            id: errorMsgId,
            role: 'assistant',
            content: '여행 정보를 불러오는 중이에요. 잠시만 기다려주세요!',
          };
          return [...prev, message];
        });
        return;
      }

      const beforeSessionVersion = sessionVersionRef.current;
      const activeSessionId = await ensureActiveSessionForSend();
      if (!activeSessionId) return;
      const requestSessionVersion = sessionVersionRef.current;

      const totalDays =
        currentSurvey?.fromDateTime && currentSurvey?.toDateTime
          ? Math.max(
              1,
              Math.ceil(
                (new Date(currentSurvey.toDateTime).getTime() - new Date(currentSurvey.fromDateTime).getTime()) /
                  (1000 * 60 * 60 * 24),
              ),
            )
          : 1;

      const userMsgId = Date.now().toString();

      // Extract focusDay from user message
      let focusDay: number | undefined;
      const dayMatch = currentUserMessage.match(/(\d+)\s*일차/);
      if (dayMatch) {
        const day = parseInt(dayMatch[1], 10);
        if (Number.isFinite(day) && day >= 1 && day <= totalDays) {
          focusDay = day;
        } else if (day > totalDays) {
          const errorMsgId = Date.now().toString();
          setConversationHistory((prev) => [
            ...prev,
            {
              id: userMsgId,
              role: 'user',
              content: currentUserMessage,
            },
            {
              id: errorMsgId,
              role: 'assistant',
              content: `이 여행은 ${totalDays}일 일정이에요. ${day}일차는 존재하지 않습니다. 1일차부터 ${totalDays}일차 중에서 선택해주세요!`,
            },
          ]);
          setIsLoading(false);
          return;
        }
      }

      const newUserMessage: ConversationMessage = {
        id: userMsgId,
        role: 'user',
        content: currentUserMessage,
      };

      const baseHistory = beforeSessionVersion === requestSessionVersion ? conversationHistory : [];
      const newHistory: ConversationMessage[] = [...baseHistory, newUserMessage];
      setConversationHistory(newHistory);
      setIsLoading(true);
      setUserInput('');
      setLastChatAt(new Date().toISOString());

      const chatDestinations = aiContext.destinations
        .filter(
          (d): d is (typeof aiContext.destinations)[number] & { id: string; day: number } =>
            typeof d.id === 'string' && typeof d.day === 'number',
        )
        .map((d) => ({
          id: d.id,
          day: d.day,
          poi: {
            name: d.poi?.name,
            latitude: d.poi?.latitude ?? undefined,
            longitude: d.poi?.longitude ?? undefined,
            placeId: d.poi?.placeId,
            poiType: d.poi?.poiType,
          },
        }));

      const chatSurvey = aiContext.currentSurvey
        ? {
            id: aiContext.currentSurvey.id,
            cities: (aiContext.currentSurvey.cities ?? []).filter(
              (c): c is { id: string; name: string; center?: { latitude: number; longitude: number } } =>
                Boolean(c && typeof c.id === 'string' && typeof c.name === 'string'),
            ),
            companion: aiContext.currentSurvey.companion,
            fromDateTime: aiContext.currentSurvey.fromDateTime,
            toDateTime: aiContext.currentSurvey.toDateTime,
          }
        : null;

      if (!chatSurvey) {
        throw new Error('Missing survey context');
      }

      const { data: res, error } = await chatApi.POST('/api/chat/sessions/{sessionId}/messages', {
        params: {
          path: { sessionId: activeSessionId },
        },
        body: {
          content: currentUserMessage,
          context: {
            destinations: chatDestinations,
            currentSurvey: chatSurvey,
            focusDay,
          },
        },
      });

      if (error || !res) {
        throw new Error('Failed to send message');
      }

      if (requestSessionVersion !== sessionVersionRef.current) return;

      const responseSessionId = res.assistantMessage.sessionId;
      const didServerRotateSession = responseSessionId !== activeSessionId;

      if (didServerRotateSession) {
        setSessionId(responseSessionId);
      }
      setLastChatAt(res.assistantMessage.createdAt);

      let responseMessage = sanitizeAssistantContent(res.assistantMessage.content || '');
      let hasError = false;

      let assistantSearchResults: SearchResultCGType[] | undefined;
      let assistantContextDay: number | undefined;

      const actions = extractActionsFromMetadata(res.assistantMessage.metadata);

      const assistantMetadata = isRecord(res.assistantMessage.metadata) ? res.assistantMessage.metadata : {};
      const assistantUi = isRecord(assistantMetadata.ui) ? assistantMetadata.ui : null;

      if (actions.length > 0) {
        for (const action of actions) {
          try {
            if (action.type === 'SEMANTIC_SEARCH_POI' || action.type === 'SEARCH_POI') {
              if (!isRecord(action.payload)) continue;
              const day = coerceNumber(action.payload.day);
              if (day) assistantContextDay = day;
              const serverResults = toSearchResults(action.payload.search_results);
              if (serverResults.length > 0) {
                assistantSearchResults = serverResults.slice(0, 10);
              }
            } else if (action.type === 'REORDER') {
              if (!isRecord(action.payload)) continue;
              const day = coerceNumber(action.payload.day);
              const newOrderIds = Array.isArray(action.payload.new_order_ids)
                ? action.payload.new_order_ids.filter((v): v is string => typeof v === 'string')
                : null;
              if (!day || !newOrderIds) continue;
              await handleReorder(day, newOrderIds);
              responseMessage += `\n\n${day}일차 일정 최적화를 완료했어요!`;
            } else if (action.type === 'DELETE') {
              if (!isRecord(action.payload)) continue;
              const id = typeof action.payload.id === 'string' ? action.payload.id : null;
              if (!id) continue;
              await handleDelete(id);
              responseMessage += '\n\n삭제를 완료했어요!';
            } else if (action.type === 'SWAP') {
              if (!isRecord(action.payload)) {
                continue;
              }
              const destId1 =
                typeof action.payload.destination_id_1 === 'string' ? action.payload.destination_id_1 : null;
              const destId2 =
                typeof action.payload.destination_id_2 === 'string' ? action.payload.destination_id_2 : null;
              if (!destId1 || !destId2) {
                continue;
              }
              await handleSwap(destId1, destId2);
            }
          } catch (actionError) {
            console.error('Action execution error:', actionError);
            hasError = true;
            responseMessage += '\n\n앗, 작업 중 문제가 생겼어요. 다시 시도해주세요!';
          }
        }
      }

      const assistantMsgId = (Date.now() + 1).toString();
      const assistantMessage: ConversationMessage = {
        id: assistantMsgId,
        role: 'assistant',
        content: responseMessage,
        isTyping: true,
        displayedContent: '',
        metadata: {
          searchResults: assistantSearchResults,
          actions: actions,
          day: assistantContextDay,
          previewImageUrl:
            typeof assistantMetadata.previewImageUrl === 'string' ? assistantMetadata.previewImageUrl : undefined,
          previewImageSourceUrl:
            typeof assistantMetadata.previewImageSourceUrl === 'string'
              ? assistantMetadata.previewImageSourceUrl
              : undefined,
          ui: assistantUi
            ? {
                kind: assistantUi.kind as string,
                totalDays: typeof assistantUi.totalDays === 'number' ? assistantUi.totalDays : undefined,
              }
            : undefined,
        },
      };

      const effectiveUserHistory = didServerRotateSession ? [newUserMessage] : newHistory;
      const next = [...effectiveUserHistory, assistantMessage];
      setConversationHistory(next);
      startTypingAnimation(assistantMsgId, responseMessage);

      if (hasError) {
        setTimeout(() => {
          setConversationHistory((prev) =>
            prev.map((msg) =>
              msg.id === assistantMsgId
                ? { ...msg, content: msg.content + '\n\n다시 한 번 시도해주시거나 다른 요청을 해주세요!' }
                : msg,
            ),
          );
        }, 500);
      }
    } catch (error) {
      console.error('AI request error:', error);
      const errorMsgId = Date.now().toString();
      setConversationHistory((prev) => {
        const message: ConversationMessage = {
          id: errorMsgId,
          role: 'assistant',
          content: '앗, AI 요청 중 문제가 생겼어요.\n\n네트워크 연결을 확인하고 다시 시도해주세요!',
        };
        return [...prev, message];
      });
    } finally {
      setIsLoading(false);
      isSendingRef.current = false;
    }
  };

  return (
    <div className='flex flex-col w-full h-full bg-white'>
      <div className='flex items-center justify-between border-b p-4'>
        <h3 className='font-semibold'>플래너 AI</h3>
        <div className='flex items-center gap-2'>
          <button
            type='button'
            onClick={() => {
              void startNewSession('manual');
            }}
            className='rounded-md border border-gray-200 bg-white px-2.5 py-1.5 text-xs text-gray-700 hover:bg-gray-50 hover:border-gray-300'
          >
            새 대화
          </button>
          <button onClick={() => setIsChatOpen(false)} className='text-gray-500 hover:text-gray-700'>
            ✕
          </button>
        </div>
      </div>

      <div className='flex-1 overflow-y-auto p-4 space-y-3'>
        {conversationHistory.length === 0 && !isLoading && (
          <div className='text-center text-sm text-gray-500'>
            <p>챗봇을 이용해 일정을 더 편리하게 관리해보세요!</p>
          </div>
        )}

        <div className='space-y-2'>
          <div className='text-xs text-gray-600 font-medium px-1'>자주 사용하는 기능</div>
          <div className='grid grid-cols-1 gap-2'>
            <div className='rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700'>
              <div className='font-medium'>📍 장소 추천받기</div>
              <div className='text-xs text-gray-500 mt-1'>추천받고 싶은 카테고리를 선택해보세요</div>
              <div className='mt-2 flex flex-wrap gap-2'>
                {(['카페', '식당', '명소', '활동', '쇼핑', '숙소'] as const).map((category) => (
                  <button
                    key={category}
                    type='button'
                    onClick={() => {
                      void handleSendMessage(`${category} 추천해줘`);
                    }}
                    className='inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors'
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            <button
              type='button'
              onClick={() => {
                const promptMsgId = `${Date.now()}-add-place-prompt`;
                isPendingAddPlaceRef.current = true;
                setConversationHistory((prev) => [
                  ...prev,
                  {
                    id: promptMsgId,
                    role: 'assistant',
                    content:
                      '어느 장소를 추가하고 싶으신가요? 장소 이름이나 카테고리를 입력해주세요.\n(예: 스타벅스 강남점, 분위기 좋은 카페)',
                  },
                ]);
              }}
              className='rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-xs text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors w-full'
            >
              <div className='font-medium text-sm'>➕ 장소 추가</div>
              <div className='text-[11px] text-gray-500 mt-0.5'>가고 싶은 장소를 일정에 바로 추가해요</div>
            </button>

            {isSemanticSupportedZone && (
              <div className='rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700'>
                <div className='font-medium'>✨ 특별한 장소 찾기</div>
                <div className='text-xs text-gray-500 mt-1'>분위기나 상황을 말하면 딱 맞는 곳을 찾아드려요</div>
                <div className='mt-2 flex flex-wrap gap-2'>
                  {[
                    '분위기 좋은 카페',
                    '인스타 감성 카페',
                    '아이랑 가기 좋은 카페',
                    '혼밥 하기 좋은 식당',
                    '바다가 보이는 레스토랑',
                    '반려동물 동반 카페',
                  ].map((query) => (
                    <button
                      key={query}
                      type='button'
                      onClick={() => {
                        void handleSendMessage(query);
                      }}
                      className='inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors'
                    >
                      {query}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <button
              onClick={() => {
                void handleSendMessage('장소 자세히 보여줘');
              }}
              className='rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors'
            >
              <div className='font-medium'>🔍 장소 자세히 보기</div>
              <div className='text-xs text-gray-500 mt-1'>궁금한 장소의 정보와 후기를 확인해보세요</div>
            </button>

            <button
              onClick={() => {
                void handleSendMessage('동선 최적화해줘');
              }}
              className='rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors'
            >
              <div className='font-medium'>🔄 동선 최적화하기</div>
              <div className='text-xs text-gray-500 mt-1'>장소 간 이동 거리를 계산해 동선을 최적화해드려요</div>
            </button>

            {destinations && destinations.length > 0 && (
              <button
                onClick={() => {
                  void handleSendMessage('장소 삭제해줘');
                }}
                className='rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors'
              >
                <div className='font-medium'>🗑️ 장소 삭제하기</div>
                <div className='text-xs text-gray-500 mt-1'>일정에서 빼고 싶은 장소를 알려주세요</div>
              </button>
            )}

            {destinations && destinations.length >= 2 && (
              <button
                onClick={() => {
                  void handleSendMessage('장소 순서 바꿔줘');
                }}
                className='rounded-lg border border-gray-200 bg-white px-4 py-3 text-left text-sm text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors'
              >
                <div className='font-medium'>↔️ 장소 순서 바꾸기</div>
                <div className='text-xs text-gray-500 mt-1'>두 장소의 위치를 서로 바꿔 일정을 재배치해요</div>
              </button>
            )}
          </div>
        </div>

        {conversationHistory.map((msg, msgIndex) => {
          const promptActive = isPromptActive(msgIndex);

          return (
            <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div
                className={`max-w-[85%] rounded-lg p-3 ${
                  msg.role === 'user' ? 'bg-primary text-white' : 'bg-gray-100 text-gray-800'
                }`}
              >
                {msg.role === 'assistant' &&
                  typeof msg.metadata?.previewImageUrl === 'string' &&
                  msg.metadata.previewImageUrl && (
                    <div className='mb-3'>
                      <a
                        href={msg.metadata.previewImageSourceUrl || msg.metadata.previewImageUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='block'
                      >
                        <Image
                          src={msg.metadata.previewImageUrl}
                          alt='장소 사진'
                          width={260}
                          height={195}
                          className='w-full max-w-[260px] rounded-md border border-gray-200 object-cover'
                          unoptimized
                        />
                      </a>
                    </div>
                  )}

                <p className='whitespace-pre-wrap text-sm'>
                  {renderMessageContent(
                    msg.isTyping && msg.displayedContent !== undefined ? msg.displayedContent : msg.content,
                    msg.role,
                  )}
                </p>

                {isDaySelectionPrompt(msg) && promptActive && (
                  <div className='mt-3'>
                    <div className='flex flex-wrap gap-2'>
                      {Array.from(
                        { length: totalDays <= 7 || expandedDayPickerId === msg.id ? totalDays : 7 },
                        (_, i) => i + 1,
                      ).map((day) => (
                        <button
                          key={day}
                          type='button'
                          disabled={isLoading}
                          onClick={() => {
                            setExpandedDayPickerId(null);
                            void handleSendMessage(`${day}일차`);
                          }}
                          className='inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors disabled:opacity-60'
                        >
                          {day}일차
                        </button>
                      ))}

                      {totalDays > 7 && expandedDayPickerId !== msg.id && (
                        <button
                          type='button'
                          onClick={() => setExpandedDayPickerId(msg.id)}
                          className='inline-flex items-center rounded-full border border-gray-200 bg-white px-3 py-1 text-xs text-gray-700 hover:bg-gray-50 hover:border-primary/30 transition-colors'
                        >
                          더보기
                        </button>
                      )}
                    </div>
                  </div>
                )}

                {isWhichSelectionPrompt(msg) &&
                  promptActive &&
                  (() => {
                    const actionType = msg.metadata?.ui?.actionType;
                    const candidates = msg.metadata?.ui?.candidates ?? [];

                    if (actionType === 'SWAP') {
                      const swapCandidates = candidates.filter(
                        (c): c is DestinationCandidate => 'id' in c && typeof c.day === 'number',
                      );

                      return (
                        <div className='mt-3'>
                          <div className='text-xs text-gray-600 mb-2'>
                            {swapSelectedIndices.length === 0
                              ? '첫 번째 장소를 선택하세요'
                              : swapSelectedIndices.length === 1
                                ? '두 번째 장소를 선택하세요'
                                : '두 장소가 선택되었습니다'}
                          </div>
                          <div className='space-y-2'>
                            {swapCandidates.map((c, idx) => {
                              const isSelected = swapSelectedIndices.includes(idx);
                              const selectionOrder = swapSelectedIndices.indexOf(idx);
                              return (
                                <button
                                  key={`${c.id}_${idx}`}
                                  type='button'
                                  disabled={isLoading || (swapSelectedIndices.length >= 2 && !isSelected)}
                                  onClick={() => {
                                    if (isSelected) {
                                      setSwapSelectedIndices(swapSelectedIndices.filter((i) => i !== idx));
                                    } else if (swapSelectedIndices.length < 2) {
                                      const newSelection = [...swapSelectedIndices, idx];
                                      setSwapSelectedIndices(newSelection);
                                      if (newSelection.length === 2) {
                                        void handleSendMessage(`${newSelection[0] + 1}번과 ${newSelection[1] + 1}번`);
                                        setSwapSelectedIndices([]);
                                      }
                                    }
                                  }}
                                  className={`w-full rounded-lg border px-3 py-2 text-left text-sm transition-colors ${
                                    isSelected
                                      ? 'border-primary bg-primary/10 text-primary font-medium'
                                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50'
                                  } ${swapSelectedIndices.length >= 2 && !isSelected ? 'opacity-40' : ''} disabled:opacity-60`}
                                >
                                  <div className='flex items-center justify-between'>
                                    <div>
                                      <span className='font-medium'>{c.day}일차</span>
                                      <span className='mx-2 text-gray-300'>|</span>
                                      <span>{c.name}</span>
                                    </div>
                                    {isSelected && (
                                      <span className='inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-white text-xs font-bold'>
                                        {selectionOrder + 1}
                                      </span>
                                    )}
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          <button
                            type='button'
                            disabled={isLoading}
                            onClick={() => {
                              setSwapSelectedIndices([]);
                              void handleSendMessage('취소');
                            }}
                            className='mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors'
                          >
                            취소
                          </button>
                        </div>
                      );
                    }

                    const isPoiDetails =
                      msg.metadata?.ui?.actionType === 'GET_POI_DETAILS' || candidates.some((c) => 'placeId' in c);

                    const hasCandidates = candidates.length > 0;

                    return (
                      <div className='mt-3'>
                        {!isPoiDetails && hasCandidates && (
                          <div className='text-xs text-gray-600 mb-2'>어떤 항목을 선택할까요?</div>
                        )}
                        <div className='space-y-2'>
                          {candidates.map((c, idx) => {
                            const key = 'id' in c ? c.id : c.placeId;
                            const dayLabel = typeof c.day === 'number' ? `${c.day}일차` : null;

                            return (
                              <button
                                key={`${key}_${idx}`}
                                type='button'
                                disabled={isLoading}
                                onClick={() => {
                                  void handleSendMessage(`${idx + 1}번`);
                                }}
                                className='w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-left text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors'
                              >
                                {dayLabel && (
                                  <>
                                    <span className='font-medium'>{dayLabel}</span>
                                    <span className='mx-2 text-gray-300'>|</span>
                                  </>
                                )}
                                <span>{c.name}</span>
                              </button>
                            );
                          })}
                        </div>
                        {!isPoiDetails && hasCandidates && (
                          <button
                            type='button'
                            disabled={isLoading}
                            onClick={() => {
                              void handleSendMessage('취소');
                            }}
                            className='mt-2 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors'
                          >
                            취소
                          </button>
                        )}
                      </div>
                    );
                  })()}

                {isConfirmationPrompt(msg) && promptActive && (
                  <div className='mt-3'>
                    {msg.metadata?.pendingAction && (
                      <div className='mb-3 rounded-lg border border-gray-200 bg-gray-50 p-3 text-sm'>
                        {msg.metadata.pendingAction.type === 'DELETE' && (
                          <div>
                            <span className='font-medium text-red-600'>삭제될 장소:</span>
                            <div className='mt-1 text-gray-700'>
                              📍 {(msg.metadata.pendingAction.payload as { name?: string }).name || '선택한 장소'}
                            </div>
                          </div>
                        )}
                        {msg.metadata.pendingAction.type === 'SWAP' && (
                          <div>
                            <span className='font-medium text-blue-600'>순서 변경</span>
                            <div className='mt-1 text-gray-600 text-xs'>두 장소의 순서가 바뀝니다</div>
                          </div>
                        )}
                        {msg.metadata.pendingAction.type === 'REORDER' && (
                          <div>
                            <span className='font-medium text-blue-600'>일정 최적화</span>
                            <div className='mt-1 text-gray-600 text-xs'>
                              {(msg.metadata.pendingAction.payload as { day?: number }).day}일차 일정이 최적화됩니다
                            </div>
                          </div>
                        )}
                      </div>
                    )}
                    <div className='flex gap-2'>
                      <button
                        type='button'
                        disabled={isLoading}
                        onClick={() => {
                          void handleSendMessage('취소');
                        }}
                        className='flex-1 rounded-lg border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-60 transition-colors'
                      >
                        취소
                      </button>
                      <button
                        type='button'
                        disabled={isLoading}
                        onClick={() => {
                          void handleSendMessage('네!');
                        }}
                        className={`flex-1 rounded-lg px-3 py-1.5 text-sm text-white disabled:opacity-60 transition-colors ${
                          msg.metadata?.ui?.actionType === 'DELETE'
                            ? 'bg-red-500 hover:bg-red-600'
                            : 'bg-primary hover:bg-primary/90'
                        }`}
                      >
                        {msg.metadata?.ui?.actionType === 'DELETE'
                          ? '삭제하기'
                          : msg.metadata?.ui?.actionType === 'SWAP'
                            ? '순서 바꾸기'
                            : '최적화'}
                      </button>
                    </div>
                  </div>
                )}

                {msg.role === 'assistant' &&
                  msg.metadata?.searchResults &&
                  msg.metadata.searchResults.length > 0 &&
                  (() => {
                    const pageSize = 5;
                    const totalPages = Math.ceil(msg.metadata.searchResults.length / pageSize);
                    const requestedPage = resultPageByMessageId[msg.id] ?? 0;
                    const currentPage = Math.min(Math.max(requestedPage, 0), Math.max(totalPages - 1, 0));
                    const startIndex = currentPage * pageSize;
                    const pageResults = msg.metadata.searchResults.slice(startIndex, startIndex + pageSize);

                    return (
                      <div className='mt-3 space-y-2'>
                        <div className='flex items-center justify-between'>
                          <div className='text-xs text-gray-600'>
                            {msg.metadata.day
                              ? `💡 '일정에 추가'를 눌러 넣을 날짜를 선택하세요 (기본: ${msg.metadata.day}일차)`
                              : "💡 마음에 드는 곳을 고르고 '일정에 추가'를 눌러주세요"}
                          </div>
                          {totalPages > 1 && (
                            <div className='flex items-center gap-1'>
                              <button
                                type='button'
                                disabled={currentPage === 0}
                                onClick={() => {
                                  setResultPageByMessageId((prev) => ({
                                    ...prev,
                                    [msg.id]: Math.max(currentPage - 1, 0),
                                  }));
                                }}
                                className='rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50'
                              >
                                ←
                              </button>
                              <span className='text-[11px] text-gray-500'>
                                {currentPage + 1}/{totalPages}
                              </span>
                              <button
                                type='button'
                                disabled={currentPage >= totalPages - 1}
                                onClick={() => {
                                  setResultPageByMessageId((prev) => ({
                                    ...prev,
                                    [msg.id]: Math.min(currentPage + 1, totalPages - 1),
                                  }));
                                }}
                                className='rounded border border-gray-200 bg-white px-2 py-0.5 text-xs text-gray-600 hover:bg-gray-50 disabled:opacity-50'
                              >
                                →
                              </button>
                            </div>
                          )}
                        </div>
                        {pageResults.map((poi, index) => (
                          <div key={index} className='rounded-lg border border-gray-200 bg-white p-3'>
                            <div className='flex gap-3 mb-2'>
                              <div className='flex-shrink-0 w-[60px] h-[60px] rounded-md overflow-hidden bg-gray-100'>
                                <Image
                                  src={
                                    poi.mainImageUrl ||
                                    'https://hoolzzuk-public-assets.s3.ap-northeast-2.amazonaws.com/custom-poi/custom_restaurant%402x.png'
                                  }
                                  alt={poi.name || '장소 이미지'}
                                  width={60}
                                  height={60}
                                  className='w-full h-full object-cover'
                                  unoptimized
                                />
                              </div>
                              <div className='flex-1 min-w-0'>
                                <div className='font-medium text-sm truncate'>{poi.name}</div>
                                {poi.address && (
                                  <div className='text-xs text-gray-500 mt-1 line-clamp-2'>{poi.address}</div>
                                )}
                              </div>
                            </div>
                            <div className='flex gap-2'>
                              <button
                                onClick={() => {
                                  void (async () => {
                                    const placeId = poi.placeId;

                                    if (!placeId) {
                                      setCenterToastModal({
                                        renderingTime: 1500,
                                        text: '장소 정보를 불러올 수 없습니다.',
                                      });
                                      return;
                                    }

                                    try {
                                      const result = await getPoiDetailByPlaceId(placeId);
                                      if (result?.data?.id) {
                                        window.open(`/poi/${result.data.id}?from=ai-assistant`, '_blank');
                                        return;
                                      }

                                      if (result?.error) {
                                        throw new Error(result.error.message);
                                      }

                                      setCenterToastModal({
                                        renderingTime: 1500,
                                        text: '장소 정보를 불러올 수 없습니다.',
                                      });
                                    } catch (error) {
                                      console.error(error);
                                      setCenterToastModal({
                                        renderingTime: 1500,
                                        text: '장소 정보를 불러올 수 없습니다.',
                                      });
                                    }
                                  })();
                                }}
                                className='flex-1 rounded border border-gray-300 bg-white px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50'
                              >
                                상세보기
                              </button>
                              <button
                                onClick={() => {
                                  setPendingAddPoi({ messageId: msg.id, poi, suggestedDay: msg.metadata?.day });
                                }}
                                className='flex-1 rounded bg-primary px-3 py-1.5 text-xs text-white hover:bg-primary/90'
                              >
                                일정에 추가
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()}
              </div>
            </div>
          );
        })}

        {pendingAddPoi && (
          <div className='flex justify-start'>
            <div className='max-w-[85%] rounded-lg bg-gray-100 p-3'>
              <p className='text-sm text-gray-800'>
                &quot;{pendingAddPoi.poi.name || '선택한 장소'}&quot;를 어디에 넣을까요?
              </p>
              <div className='mt-2 flex flex-wrap gap-2'>
                {Array.from({ length: totalDays }, (_, i) => i + 1).map((day) => (
                  <button
                    key={day}
                    type='button'
                    disabled={isLoading}
                    onClick={() => confirmAddPoiToDay(pendingAddPoi.poi, day)}
                    className={`rounded border bg-white px-2 py-1 text-xs hover:bg-primary/10 disabled:opacity-60 ${
                      pendingAddPoi.suggestedDay === day
                        ? 'border-primary text-primary'
                        : 'border-gray-300 text-gray-700'
                    }`}
                  >
                    {day}일차
                  </button>
                ))}
                <button
                  type='button'
                  disabled={isLoading}
                  onClick={() => setPendingAddPoi(null)}
                  className='rounded border border-gray-300 bg-white px-2 py-1 text-xs text-gray-500 hover:bg-gray-50 disabled:opacity-60'
                >
                  취소
                </button>
              </div>
            </div>
          </div>
        )}

        {isLoading && (
          <div className='flex justify-start'>
            <div className='max-w-[85%] rounded-lg bg-gray-100 p-3'>
              <p className='text-sm text-gray-500'>응답을 생성하는 중...</p>
            </div>
          </div>
        )}

        <div ref={messagesEndRef} />
      </div>

      <div className='border-t p-4'>
        <div className='flex gap-2'>
          <input
            type='text'
            value={userInput}
            onChange={(e) => setUserInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key !== 'Enter') return;
              if (e.nativeEvent.isComposing) return;
              e.preventDefault();
              void handleSendMessage();
            }}
            placeholder='예: 2일차 동선 최적화해줘'
            className='flex-1 rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary'
            disabled={isLoading}
          />
          <button
            onClick={() => {
              void handleSendMessage();
            }}
            disabled={isLoading}
            className='rounded-lg bg-primary px-4 py-2 text-sm text-white hover:bg-primary/90 disabled:bg-gray-300'
          >
            {isLoading ? '...' : '전송'}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function AiAssistantFloatingButton() {
  const isChatOpen = useAtomValue(aiChatOpenAtom);

  return <>{!isChatOpen && <AiAssistantButton />}</>;
}
